import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { createFileRoute, Outlet } from '@tanstack/react-router';

import type { StatsPeriod } from '@/api/statsApi';
import type { StatsScope } from '@/components/map/utils/resolveStatsScope';
import { ProductGuard } from '@/components/upsell/ProductGuard';
import { MapActionsProvider } from '@/map-actions/MapActionsProvider';
import { MapInteractionProvider } from '@/map-interaction/MapInteractionProvider';
import { ExclusivePanelProvider } from '@/panels/ExclusivePanelProvider';
import { TableViewProvider } from '@/table-view/TableViewProvider';
import { useTableView } from '@/table-view/useTableView';
import { useExclusivePanel } from '@/panels/useExclusivePanel';
import { useMobileChrome } from '@/components/app/useMobileChrome';
import { StatsBarSlotProvider } from '@/stats/StatsBarSlotContext';
import { StatsScopeProvider } from '@/stats/StatsScopeProvider';
import { StatsFiltersProvider } from '@/stats/StatsFiltersProvider';
import { useMobileBottomSlot } from '@/stats/useMobileBottomSlot';
import { useStatsScope } from '@/stats/useStatsScope';
import { StatsBar } from '@/components/stats/StatsBar';
import { StatsConfigPanel } from '@/components/stats/StatsConfigPanel';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useNetworkStats } from '@/hooks/useNetworkStats';
import { useScopedStats } from '@/hooks/useScopedStats';
import { useStatsPreferences } from '@/hooks/useStatsPreferences';

function FindAndFixLayout() {
    return (
        <ProductGuard product="find-and-fix">
            <ExclusivePanelProvider>
                <StatsScopeProvider>
                    <StatsFiltersProvider>
                        <MapActionsProvider>
                            <TableViewProvider>
                                {/* Innermost: owns the map-interaction slice. Living in the
                                    layout route means selection/highlights survive sub-route
                                    round-trips, like the table layout above it. */}
                                <MapInteractionProvider>
                                    <FindAndFixContent />
                                </MapInteractionProvider>
                            </TableViewProvider>
                        </MapActionsProvider>
                    </StatsFiltersProvider>
                </StatsScopeProvider>
            </ExclusivePanelProvider>
        </ProductGuard>
    );
}

/** Human-readable name for a scoped stats context (AOI name / DMA name). */
function scopeDisplayName(scope: StatsScope | null): string | null {
    if (!scope || scope.type === 'network') return null;
    return scope.type === 'area' ? scope.name : scope.name || scope.ident;
}

function FindAndFixContent() {
    // Stats is one of four mutually-exclusive panels; its open state is derived
    // from the shared signal so opening a map panel (Layers/Filters/POI) closes
    // it and vice versa. See ExclusivePanelProvider.
    const { activePanel, setActivePanel } = useExclusivePanel();
    const isStatsPanelOpen = activePanel === 'stats';
    const [period, setPeriod] = useState<StatsPeriod>('overall');
    const stats = useNetworkStats(period);
    const statsPreferences = useStatsPreferences();
    const isDesktop = useMediaQuery('(min-width: 768px)');

    // Context-aware stats: MapView resolves the AOI/DMA under the map centre and
    // publishes it through StatsScopeProvider; the scoped totals are fetched here.
    // Network stats stay fetched regardless — they provide the "% of total"
    // denominators and the fallback whenever no scoped data is available (network
    // scope, scoped 404, scoped error, or first scoped load).
    const { scope } = useStatsScope();
    const scopedStats = useScopedStats(scope, period);
    const showScoped = scope.type !== 'network' && scopedStats.data !== null && scopedStats.dataScope !== null;

    // On a phone an open table is always maximised and full-screen, and RootLayout's floating
    // sidebar trigger would sit on top of its title — so the table claims that corner while open.
    // The cleanup releases it on close, on rotation to desktop and on route unmount alike.
    const { isOpen: isTableOpen } = useTableView();
    const { setHideMobileTrigger } = useMobileChrome();
    useEffect(() => {
        if (isDesktop || !isTableOpen) return;
        setHideMobileTrigger(true);
        return () => {
            setHideMobileTrigger(false);
        };
    }, [isDesktop, isTableOpen, setHideMobileTrigger]);

    const mobileSlot = useMobileBottomSlot();

    const statsBar = (
        <StatsBar
            data={showScoped ? scopedStats.data : stats.data}
            networkTotals={showScoped ? stats.data : null}
            scopeLabel={showScoped ? scopeDisplayName(scopedStats.dataScope) : null}
            isLoading={stats.isLoading || scopedStats.isLoading}
            // Scoped failures fall back to the network view silently; only a
            // network-stats failure surfaces the bar's error state.
            isError={stats.isError}
            visibleStats={statsPreferences.visibleStats}
            onOpenConfig={() => {
                setActivePanel(isStatsPanelOpen ? null : 'stats');
            }}
        />
    );

    return (
        // Desktop: hand the StatsBar to MapView via context so it renders above the map
        // only — every right-hand panel then sits full-height beside the [bar + map] column.
        // Null on mobile (the bar is portalled into the layout slot below instead).
        <StatsBarSlotProvider value={isDesktop ? statsBar : null}>
            <div className="absolute inset-0 flex flex-col overflow-hidden">
                <div className="flex min-h-0 flex-1">
                    <div className="relative min-h-0 flex-1">
                        <Outlet />
                    </div>
                    <StatsConfigPanel
                        isOpen={isStatsPanelOpen}
                        visibleStats={statsPreferences.visibleStats}
                        onToggle={statsPreferences.toggleStat}
                        onClose={() => {
                            setActivePanel(null);
                        }}
                        period={period}
                        onPeriodChange={setPeriod}
                    />
                </div>

                {/* Mobile: portal StatsBar into the layout-level slot (order-3, below AppHeader).
                    The slot div lives in RootLayout so it can participate in the top-level flex
                    column at the right order — impossible from inside <main>.  (Finding 9) */}
                {!isDesktop && mobileSlot && createPortal(statsBar, mobileSlot)}
            </div>
        </StatsBarSlotProvider>
    );
}

export const Route = createFileRoute('/find-and-fix')({
    component: FindAndFixLayout,
});
